// OrgApplicationPortal.js - Sports Org Tier B Certification Application
// InvestorQ Private Market — OffBeet Media & Tech Inc.
// v2.0 — Live cert score preview + readiness checklist + compliance disclosures

import React, { useState, useEffect, useMemo } from 'react';
import {
  Trophy, Lock, CheckCircle, AlertCircle, ChevronRight,
  ChevronLeft, Upload, FileText, Building, Users, DollarSign,
  Globe, Mail, Phone, Shield, Star, ArrowRight, Info,
  Calendar, BarChart2, Tv, Ticket, Award, Zap, X, Check
} from 'lucide-react';

// ── v2.0: Certification scoring engine ───────────────────────────────────────
import { computeCertScore, scoreMeta, buildCertChecklist } from './CertificationEngine';

// ── v2.0: Compliance disclosures ─────────────────────────────────────────────
import { TierDisclosure } from './ComplianceDisclosures';

// ── API URL ───────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const handler = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return size;
}



// ─── Tier definitions ──────────────────────────────────────────────────────
const TIERS = {
  A: {
    label: 'Tier A',
    name: 'Market Intelligence',
    color: '#00d4aa',
    bg: 'rgba(0,212,170,0.08)',
    border: 'rgba(0,212,170,0.25)',
    icon: '◎',
    desc: 'Editorial estimates using public data. No org consent required. Great for initial discovery.',
    benefits: ['Public data valuation estimate','Shadow token issuance (simulated)','Watcher count tracking','Intelligence feed inclusion'],
    requires: ['No action needed — InvestorQ may list any public organization at Tier A'],
    fee: 'Free',
    timeline: 'Immediate'
  },
  B: {
    label: 'Tier B',
    name: 'Certified Intelligence',
    color: '#7c6fff',
    bg: 'rgba(124,111,255,0.08)',
    border: 'rgba(124,111,255,0.25)',
    icon: '◈',
    desc: 'Org-verified financials under NDA. Deal room access. Enhanced confidence score. Investor relations tools.',
    benefits: [
      'Verified valuation (AICPA-aligned, 4-method)',
      'Deal room (up to 20 documents)',
      'NDA-gated investor access',
      'Shadow token with verified price anchoring',
      'InvestorQ Certified badge',
      'Quarterly IR update publishing',
      'Investor relations dashboard',
      'Priority placement in market feed'
    ],
    requires: [
      'Completed application (this form)',
      'Executed NDA with InvestorQ',
      '2 years GAAP financials (reviewed or audited)',
      'Org ownership/cap table summary',
      'League or franchise overview document'
    ],
    fee: '$5,000 / year',
    timeline: '2–3 weeks after document submission'
  },
  C: {
    label: 'Tier C',
    name: 'Live Investment Raise',
    color: '#f5a623',
    bg: 'rgba(245,166,35,0.08)',
    border: 'rgba(245,166,35,0.25)',
    icon: '◆',
    desc: 'Real Reg CF / Reg A+ raise via licensed broker-dealer partner. All Tier B benefits included.',
    benefits: [
      'All Tier B benefits',
      'Reg CF raise (up to $5M) via DealMaker/Republic',
      'Reg A+ raise (up to $75M) — Tier 2',
      'Embedded raise widget on InvestorQ',
      'Investor KYC & escrow management',
      'Post-raise cap table update',
      'Featured raise card on platform',
      'Email blast to relevant investor segment'
    ],
    requires: [
      'Active Tier B certification',
      'Audited GAAP financials (for raises >$618K)',
      'SEC Form C filed with funding portal partner',
      'Broker-dealer partnership agreement',
      'Bad actor certification'
    ],
    fee: '5% success fee on gross raise proceeds',
    timeline: '4–6 weeks from Tier B certification'
  }
};

const SPORTS = [
  'Cricket','Football (NFL)','Basketball (NBA)','Soccer (MLS/NWSL)',
  'Baseball (MLB)','Hockey (NHL)','Tennis','Golf','Esports',
  'Rugby','Volleyball','Track & Field','Swimming','Combat Sports',
  'Cricket (International)','Soccer (International)','Other'
];

const ORG_TYPES = [
  'Professional League','Franchise / Club','Sports Academy',
  'Media Rights Holder','Venue / Stadium Operator','Sports Technology',
  'Esports Organization','Youth Development League','Other'
];

const REVENUE_BANDS = [
  '<$500K','$500K–$2M','$2M–$10M','$10M–$50M','$50M+'
];

const RAISE_RANGES = [
  'Not currently seeking capital','Under $500K (Reg CF)',
  '$500K – $1M (Reg CF)','$1M – $5M (Reg CF max)',
  '$5M – $20M (Reg A+ Tier 1)','$20M – $75M (Reg A+ Tier 2)',
  'Over $75M (Private placement / Reg D)'
];

// ─── Step config ──────────────────────────────────────────────────────────
const STEPS = [
  { id: 'tier',     label: 'Choose Tier',       icon: Star },
  { id: 'org',      label: 'Org Profile',        icon: Building },
  { id: 'finance',  label: 'Financial Summary',  icon: DollarSign },
  { id: 'media',    label: 'Media & Fan Base',   icon: Tv },
  { id: 'raise',    label: 'Capital Goals',      icon: BarChart2 },
  { id: 'docs',     label: 'Documents',          icon: FileText },
  { id: 'contact',  label: 'Contact & Submit',   icon: Mail },
];

// ─── Field helper ────────────────────────────────────────────────────────
const Field = ({ label, required, hint, children }) => (
  <div style={{ marginBottom: '18px' }}>
    <label style={{
      display: 'block', fontSize: '11px', fontWeight: '700',
      color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em',
      marginBottom: '6px'
    }}>
      {label}{required && <span style={{ color: '#f5a623', marginLeft: '3px' }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: '10px', color: '#475569', marginTop: '4px', fontStyle: 'italic' }}>{hint}</div>}
  </div>
);

// ── Field error message helper ───────────────────────────────────────────
const ErrorMsg = ({ msg }) => msg ? (
  <div style={{ fontSize:'10px', color:'#f87171', marginTop:'4px', display:'flex', gap:'4px', alignItems:'center' }}>
    <span>⚠</span> {msg}
  </div>
) : null;

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(15,23,42,0.7)', border: '1px solid #334155',
  borderRadius: '8px', padding: '10px 14px', color: '#e2e8f0',
  fontSize: '13px', outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif'
};

const selectStyle = { ...inputStyle, cursor: 'pointer' };

const Input = (props) => (
  <input style={inputStyle} {...props} />
);

const Select = ({ children, ...props }) => (
  <select style={selectStyle} {...props}>{children}</select>
);

const Textarea = (props) => (
  <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }} {...props} />
);

// ─── Main Component ───────────────────────────────────────────────────────
const OrgApplicationPortal = ({ setActiveTab }) => {
  const { w } = useWindowSize();
  const isMobile = w < 768;
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);   // ← NEW: loading state
  const [submitError, setSubmitError] = useState('');    // ← NEW: error state
  const [fieldErrors, setFieldErrors]   = useState({});    // field-level validation errors
  const [appId, setAppId] = useState(null);              // ← NEW: returned app ID
  const [form, setForm] = useState({
    // Tier
    selectedTier: 'B',
    // Org profile
    orgName: '', orgType: '', sport: '', country: 'United States',
    founded: '', website: '', description: '',
    numTeams: '', numAthletes: '', seasonsCompleted: '',
    // Finance
    revenueBand: '', ebitdaPositive: '', revenueTrend: '',
    hasAuditedFinancials: '', hasCpa: '',
    // Media & fans
    ticketSales: '', avgAttendance: '', socialFollowers: '',
    streamingViews: '', tvDeal: '', sponsorRevenue: '',
    // Capital goals
    raiseRange: '', raiseUse: '', timeline: '', priorRaises: '',
    // Docs
    hasFinancials: false, hasCapTable: false,
    hasOrgDeck: false, hasSponsorDeck: false,
    // Contact
    firstName: '', lastName: '', title: '', email: '',
    phone: '', linkedIn: '', agreeTerms: false, agreeNda: false
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const e = (key) => (ev) => set(key, ev.target.value);

  // ── v2.0: Live certification score derived from form data ─────────────────
  const { total: certScore, breakdown: certBreakdown } = useMemo(
    () => computeCertScore(form),
    [form]
  );
  const certMeta = scoreMeta(certScore);

  const canAdvance = () => {
    if (step === 0) return !!form.selectedTier;
    if (step === 1) return form.orgName && form.orgType && form.sport;
    if (step === 2) return form.revenueBand;
    if (step === 5) return true; // docs optional
    if (step === 6) return form.firstName && form.lastName && form.email && form.agreeTerms && (form.selectedTier === 'A' || form.agreeNda);
    return true;
  };

  // ── Client-side field validation ────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!form.orgName?.trim())    errs.orgName = 'Organization name is required';
    if (!form.orgType)            errs.orgType = 'Please select an organization type';
    if (!form.sport)              errs.sport = 'Please select a sport';
    if (!form.revenueBand)        errs.revenueBand = 'Please select a revenue band';
    if (!form.firstName?.trim())  errs.firstName = 'First name is required';
    if (!form.lastName?.trim())   errs.lastName = 'Last name is required';
    if (!form.email?.trim())      errs.email = 'Email address is required';
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
                                  errs.email = 'Please enter a valid email address';
    if (form.website && !/^https?:\/\//.test(form.website)) {
      // Auto-fix: prepend https://
      form.website = 'https://' + form.website;
    }
    if (form.phone && !/^[\d\s\-\+\(\)\.]{7,20}$/.test(form.phone))
                                  errs.phone = 'Invalid phone format (digits, spaces, +, -, () only)';
    if (!form.agreeTerms)         errs.agreeTerms = 'You must agree to the Terms of Service';
    if (form.selectedTier !== 'A' && !form.agreeNda)
                                  errs.agreeNda = 'NDA agreement is required for Tier B/C';
    return errs;
  };

  // ── UPDATED: handleSubmit — posts to backend, fires emails ─────────────
  const handleSubmit = async () => {
    if (!canAdvance()) return;
    // Client-side validation first
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    setSubmitError('');

    // Build pending org for immediate local tile display
    const pendingOrg = {
      id: `pending_${Date.now()}`,
      name: form.orgName,
      orgType: form.orgType,
      sport: form.sport,
      country: form.country,
      founded: form.founded,
      website: form.website,
      description: form.description,
      revenueBand: form.revenueBand,
      raiseRange: form.raiseRange,
      tier: form.selectedTier,
      contactEmail: form.email,
      contactName: `${form.firstName} ${form.lastName}`,
      submittedAt: new Date().toISOString(),
      status: 'pending_review',
    };

    try {
      // POST to backend — triggers admin alert + applicant confirmation emails
      const res = await fetch(`${API_URL}/orgs/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');

      // Update pending org with real DB id + cert score from backend
      pendingOrg.id = `db_${data.appId}`;
      setAppId(data.appId);
      // certScore state already derived from form via useMemo — no separate set needed

    } catch (err) {
      // If backend fails, still show success — tile shows locally, team follows up manually
      console.warn('Backend submission error (non-fatal):', err.message);
      setSubmitError(err.message); // shown as soft warning, not a blocker
    }

    // Always: update localStorage + fire event for live tile display
    try {
      const existing = JSON.parse(localStorage.getItem('iq_pending_orgs') || '[]');
      existing.push(pendingOrg);
      localStorage.setItem('iq_pending_orgs', JSON.stringify(existing));
    } catch(e) { console.warn('localStorage unavailable', e); }

    window.dispatchEvent(new CustomEvent('iq:org_submitted', { detail: pendingOrg }));

    setSubmitting(false);
    setSubmitted(true);
  };

  const tier = TIERS[form.selectedTier];

  // ── Submitted confirmation ─────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ maxWidth: '660px', margin: '0 auto', padding: '40px 20px' }}>
        <div style={{
          background: 'rgba(30,41,59,0.6)', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,212,170,0.3)', borderRadius: '20px',
          padding: '48px', textAlign: 'center'
        }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
          <div style={{
            fontSize: '24px', fontWeight: '800', color: '#e2e8f0',
            marginBottom: '8px', letterSpacing: '-0.02em'
          }}>Application Submitted!</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px', lineHeight: '1.7' }}>
            Thank you, <strong style={{ color: '#94a3b8' }}>{form.firstName}</strong>.<br />
            We've received <strong style={{ color: '#94a3b8' }}>{form.orgName || 'your organization'}</strong>'s
            application for <span style={{ color: tier.color, fontWeight: '700' }}>{tier.label} — {tier.name}</span> certification.
          </div>
          {/* Application ID badge */}
          {appId && (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px' }}>
              <div style={{
                display: 'inline-block', fontSize: '11px', fontWeight: '700',
                color: '#7c6fff', background: 'rgba(124,111,255,0.1)',
                border: '1px solid rgba(124,111,255,0.25)', borderRadius: '8px',
                padding: '4px 14px'
              }}>
                Application ID #{appId}
              </div>
              {certScore > 0 && (
                <div style={{
                  display: 'inline-block', fontSize: '11px', fontWeight: '700',
                  color: certMeta.color, background: certMeta.bg,
                  border: `1px solid ${certMeta.border}`, borderRadius: '8px',
                  padding: '4px 14px'
                }}>
                  Cert Score: {certScore}/100 — {certMeta.label}
                </div>
              )}
            </div>
          )}
          {/* Soft warning if backend had an issue but form still went through locally */}
          {submitError && (
            <div style={{
              background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.2)',
              borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
              fontSize: '11px', color: '#f5a623', lineHeight: '1.6'
            }}>
              ⚠️ Note: Email notification may be delayed. Our team will still receive your application.
              For immediate assistance email <strong>info@investorq.io</strong>.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px', textAlign: 'left' }}>
            {[
              { icon: '📧', text: `Confirmation sent to ${form.email}` },
              { icon: '📋', text: 'InvestorQ team reviews within 2–3 business days' },
              form.selectedTier !== 'A' && { icon: '🔒', text: 'NDA will be sent to your email for e-signature' },
              { icon: '📂', text: 'Document checklist will be shared after NDA execution' },
              { icon: '⏱️', text: `Estimated certification: ${tier.timeline}` },
            ].filter(Boolean).map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: '12px', alignItems: 'flex-start',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '10px', padding: '12px 16px'
              }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: '12px', color: '#94a3b8', lineHeight: '1.5' }}>{item.text}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => { setSubmitted(false); setStep(0); setAppId(null); setSubmitError(''); setForm(f => ({ ...f, orgName: '', email: '' })); }}
              style={{
                padding: '10px 22px', borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                color: '#94a3b8', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
              }}>
              Submit Another
            </button>
            <button
              onClick={() => setActiveTab && setActiveTab('sports-private')}
              style={{
                padding: '10px 22px', borderRadius: '10px', border: 'none',
                background: 'linear-gradient(135deg, #00d4aa, #00b894)',
                color: '#000', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
              }}>
              View Private Market →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step renderer ─────────────────────────────────────────────────────
  const renderStep = () => {
    switch (STEPS[step].id) {

      // ── STEP 0: Choose Tier ────────────────────────────────────────────
      case 'tier':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>
              Choose Your Certification Tier
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '28px', lineHeight: '1.7' }}>
              InvestorQ's three-tier model gives sports organizations structured access to capital intelligence and investor communities.
              Start where your organization is today — you can upgrade at any time.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Object.entries(TIERS).map(([key, t]) => (
                <div
                  key={key}
                  onClick={() => set('selectedTier', key)}
                  style={{
                    border: `2px solid ${form.selectedTier === key ? t.color : 'rgba(255,255,255,0.06)'}`,
                    background: form.selectedTier === key ? t.bg : 'rgba(255,255,255,0.02)',
                    borderRadius: '14px', padding: '20px 22px', cursor: 'pointer',
                    transition: 'all 0.2s', position: 'relative'
                  }}>
                  {key === 'B' && (
                    <div style={{
                      position: 'absolute', top: '-10px', left: '20px',
                      background: 'linear-gradient(135deg, #7c6fff, #9b8fff)',
                      color: '#fff', fontSize: '9px', fontWeight: '800',
                      padding: '2px 12px', borderRadius: '10px', letterSpacing: '0.1em'
                    }}>RECOMMENDED FOR USPL</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '12px',
                        background: t.bg, border: `1px solid ${t.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '20px', color: t.color, fontWeight: '900', flexShrink: 0
                      }}>{t.icon}</div>
                      <div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '800', color: t.color }}>{t.label}</span>
                          <span style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600' }}>— {t.name}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.6', maxWidth: '480px' }}>{t.desc}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: isMobile ? 'left' : 'right', flexShrink: 0, marginLeft: isMobile ? '0' : '16px', marginTop: isMobile ? '8px' : '0' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: t.color }}>{t.fee}</div>
                      <div style={{ fontSize: '10px', color: '#475569' }}>{t.timeline}</div>
                    </div>
                  </div>

                  {form.selectedTier === key && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${t.border}` }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '9px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>What You Get</div>
                          {t.benefits.slice(0, 4).map((b, i) => (
                            <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                              <CheckCircle size={10} color={t.color} style={{ flexShrink: 0, marginTop: '2px' }} />{b}
                            </div>
                          ))}
                          {t.benefits.length > 4 && <div style={{ fontSize: '10px', color: '#475569' }}>+{t.benefits.length - 4} more</div>}
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Requirements</div>
                          {t.requires.slice(0, 4).map((r, i) => (
                            <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                              <ChevronRight size={10} color='#475569' style={{ flexShrink: 0, marginTop: '2px' }} />{r}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      // ── STEP 1: Org Profile ────────────────────────────────────────────
      case 'org':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>Organization Profile</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '28px' }}>Tell us about your organization.</div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1/-1' }}>
                <Field label="Organization Name" required>
                  <Input value={form.orgName} onChange={e('orgName')} placeholder="e.g. US Premier League Cricket" style={{...inputStyle, borderColor: fieldErrors.orgName ? '#f87171' : '#334155'}} />
                  <ErrorMsg msg={fieldErrors.orgName} />
                </Field>
              </div>
              <Field label="Organization Type" required>
                <Select value={form.orgType} onChange={e('orgType')}>
                  <option value="">Select type…</option>
                  {ORG_TYPES.map(o => <option key={o}>{o}</option>)}
                </Select>
              </Field>
              <Field label="Primary Sport" required>
                <Select value={form.sport} onChange={e('sport')}>
                  <option value="">Select sport…</option>
                  {SPORTS.map(s => <option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Country / Region" required>
                <Input value={form.country} onChange={e('country')} placeholder="United States" />
              </Field>
              <Field label="Year Founded">
                <Input type="number" value={form.founded} onChange={e('founded')} placeholder="e.g. 2018" />
              </Field>
              <Field label="Website" hint="Include https:// e.g. https://yourleague.com">
                <Input value={form.website} onChange={e('website')} placeholder="https://yourleague.com" style={{...inputStyle, borderColor: fieldErrors.website ? '#f87171' : '#334155'}} />
                <ErrorMsg msg={fieldErrors.website} />
              </Field>
              <Field label="Number of Teams / Franchises">
                <Input type="number" value={form.numTeams} onChange={e('numTeams')} placeholder="e.g. 8" />
              </Field>
              <Field label="Seasons Completed">
                <Input type="number" value={form.seasonsCompleted} onChange={e('seasonsCompleted')} placeholder="e.g. 5" />
              </Field>
              <div style={{ gridColumn: '1/-1' }}>
                <Field label="Organization Description" hint="Brief overview: mission, structure, what makes you unique (150 words max)">
                  <Textarea value={form.description} onChange={e('description')} placeholder="Describe your organization, its mission, and what makes it a compelling investment…" />
                </Field>
              </div>
            </div>
          </div>
        );

      // ── STEP 2: Financial Summary ──────────────────────────────────────
      case 'finance':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>Financial Summary</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>
              This is used to assign your initial valuation band. Exact figures are shared only after NDA execution.
            </div>
            <div style={{
              background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)',
              borderRadius: '10px', padding: '12px 16px', marginBottom: '24px',
              display: 'flex', gap: '10px', alignItems: 'flex-start'
            }}>
              <Info size={14} color="#f5a623" style={{ flexShrink: 0, marginTop: '1px' }} />
              <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: '1.6' }}>
                <strong style={{ color: '#f5a623' }}>Confidential:</strong> Financial information submitted here is used only for initial tier scoring.
                Detailed financials are collected separately under NDA for Tier B/C certification.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap: '16px' }}>
              <Field label="Annual Revenue Band" required>
                <Select value={form.revenueBand} onChange={e('revenueBand')}>
                  <option value="">Select range…</option>
                  {REVENUE_BANDS.map(r => <option key={r}>{r}</option>)}
                </Select>
              </Field>
              <Field label="Revenue Trend (Last 3 Years)">
                <Select value={form.revenueTrend} onChange={e('revenueTrend')}>
                  <option value="">Select…</option>
                  <option>Rapid growth (&gt;30% YoY)</option>
                  <option>Moderate growth (10–30% YoY)</option>
                  <option>Stable (0–10% YoY)</option>
                  <option>Declining</option>
                  <option>Pre-revenue / early stage</option>
                </Select>
              </Field>
              <Field label="Is the organization EBITDA positive?">
                <Select value={form.ebitdaPositive} onChange={e('ebitdaPositive')}>
                  <option value="">Select…</option>
                  <option>Yes — profitable</option>
                  <option>Break-even</option>
                  <option>No — investing in growth</option>
                  <option>Prefer not to disclose</option>
                </Select>
              </Field>
              <Field label="Do you have GAAP financial statements?">
                <Select value={form.hasAuditedFinancials} onChange={e('hasAuditedFinancials')}>
                  <option value="">Select…</option>
                  <option>Yes — audited by CPA</option>
                  <option>Yes — reviewed by CPA</option>
                  <option>Yes — compiled / internal</option>
                  <option>No — can prepare with help</option>
                </Select>
              </Field>
              <Field label="Do you have a CPA / accounting firm?" hint="Required for Tier B certification and all Reg CF raises.">
                <Select value={form.hasCpa} onChange={e('hasCpa')}>
                  <option value="">Select…</option>
                  <option>Yes — active relationship</option>
                  <option>No — need a referral</option>
                </Select>
              </Field>
            </div>

            {/* v2.0: Live certification score preview */}
            <div style={{
              marginTop: '24px',
              background: certMeta.bg, border: `1px solid ${certMeta.border}`,
              borderRadius: '12px', padding: '16px 18px'
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'
              }}>
                <div style={{ fontSize: '10px', fontWeight: '800', color: certMeta.color,
                  textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Certification Score Preview
                </div>
                <div style={{ fontSize: '20px', fontWeight: '900', color: certMeta.color }}>
                  {certScore}/100 <span style={{ fontSize: '11px', fontWeight: '600' }}>— {certMeta.label}</span>
                </div>
              </div>
              <div style={{ height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', marginBottom: '10px', overflow: 'hidden' }}>
                <div style={{
                  width: `${certScore}%`, height: '100%', background: certMeta.color,
                  borderRadius: '3px', transition: 'width 0.6s ease'
                }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '8px' }}>
                {certBreakdown && Object.entries(certBreakdown).map(([key, val]) => (
                  <div key={key} style={{ fontSize: '9px', color: '#64748b' }}>
                    <span style={{ color: '#94a3b8', fontWeight: '700' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </span>{' '}{val}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '10px', color: '#475569', lineHeight: '1.5' }}>
                Score improves with audited financials, higher revenue band, CPA on file, and completed documents.
                Tier B requires a minimum score of 40. Tier C raises require 70+.
              </div>
            </div>
          </div>
        );

      // ── STEP 3: Media & Fan Base ───────────────────────────────────────
      case 'media':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>Media & Fan Base</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '28px' }}>
              Fan and media metrics heavily influence brand score and investor interest. Approximate figures are fine.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap: '16px' }}>
              <Field label="Average Event Attendance" hint="Per game / match">
                <Input type="number" value={form.avgAttendance} onChange={e('avgAttendance')} placeholder="e.g. 5000" />
              </Field>
              <Field label="Annual Ticket Sales Revenue ($)">
                <Input type="number" value={form.ticketSales} onChange={e('ticketSales')} placeholder="e.g. 400000" />
              </Field>
              <Field label="Total Social Media Followers">
                <Input type="number" value={form.socialFollowers} onChange={e('socialFollowers')} placeholder="e.g. 85000" />
              </Field>
              <Field label="Annual Streaming / Digital Views">
                <Input type="number" value={form.streamingViews} onChange={e('streamingViews')} placeholder="e.g. 5100000" />
              </Field>
              <Field label="Sponsorship / Brand Revenue ($)">
                <Input type="number" value={form.sponsorRevenue} onChange={e('sponsorRevenue')} placeholder="e.g. 320000" />
              </Field>
              <Field label="TV / Streaming Broadcast Deal?" hint="Yes/No + partner name if applicable">
                <Input value={form.tvDeal} onChange={e('tvDeal')} placeholder="e.g. Yes — Willow TV, ESPN+ streaming" />
              </Field>
            </div>
          </div>
        );

      // ── STEP 4: Capital Goals ──────────────────────────────────────────
      case 'raise':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>Capital Goals</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '28px' }}>
              Tell us what you're trying to achieve. This helps us match you to the right tier and broker-dealer partner.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Target Raise Amount" required>
                <Select value={form.raiseRange} onChange={e('raiseRange')}>
                  <option value="">Select…</option>
                  {RAISE_RANGES.map(r => <option key={r}>{r}</option>)}
                </Select>
              </Field>
              <Field label="Intended Use of Capital" hint="e.g. expansion, infrastructure, player development, technology">
                <Textarea value={form.raiseUse} onChange={e('raiseUse')} placeholder="Describe how you plan to use the capital raised…" />
              </Field>
              <Field label="Target Timeline for Raise">
                <Select value={form.timeline} onChange={e('timeline')}>
                  <option value="">Select…</option>
                  <option>Within 3 months</option>
                  <option>3–6 months</option>
                  <option>6–12 months</option>
                  <option>12+ months / exploring</option>
                  <option>No active raise planned — intelligence only</option>
                </Select>
              </Field>
              <Field label="Prior Fundraising History">
                <Textarea value={form.priorRaises} onChange={e('priorRaises')} placeholder="Any prior raises? Reg CF, angel, family office, institutional? Approximate amounts and dates…" />
              </Field>
            </div>
          </div>
        );

      // ── STEP 5: Documents ─────────────────────────────────────────────
      case 'docs':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>Documents</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>
              Check which documents you have ready. Full document submission happens after NDA execution — this helps us prepare your onboarding package.
            </div>

            {form.selectedTier === 'A' && (
              <div style={{
                background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.2)',
                borderRadius: '10px', padding: '14px 16px', marginBottom: '24px',
                fontSize: '12px', color: '#94a3b8', lineHeight: '1.6'
              }}>
                <strong style={{ color: '#00d4aa' }}>Tier A</strong> — No documents required for initial listing.
                InvestorQ uses public data for editorial estimates. You can upgrade to Tier B at any time.
              </div>
            )}

            {[
              { key: 'hasFinancials', label: 'Financial Statements (2+ years GAAP)', icon: DollarSign, required: form.selectedTier !== 'A', tier: 'B+' },
              { key: 'hasCapTable', label: 'Ownership / Cap Table Summary', icon: Users, required: form.selectedTier !== 'A', tier: 'B+' },
              { key: 'hasOrgDeck', label: 'Organization Overview / Pitch Deck', icon: FileText, required: false, tier: 'B+' },
              { key: 'hasSponsorDeck', label: 'Sponsorship / Media Package', icon: Tv, required: false, tier: 'B+' },
            ].map(doc => (
              <div
                key={doc.key}
                onClick={() => set(doc.key, !form[doc.key])}
                style={{
                  display: 'flex', gap: '14px', alignItems: 'center',
                  padding: '16px 18px', borderRadius: '12px', cursor: 'pointer',
                  marginBottom: '10px', transition: 'all 0.15s',
                  background: form[doc.key] ? 'rgba(0,212,170,0.06)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${form[doc.key] ? 'rgba(0,212,170,0.3)' : 'rgba(255,255,255,0.06)'}`
                }}>
                <div style={{
                  width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0,
                  background: form[doc.key] ? '#00d4aa' : 'transparent',
                  border: `2px solid ${form[doc.key] ? '#00d4aa' : '#334155'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {form[doc.key] && <CheckCircle size={13} color="#000" />}
                </div>
                <doc.icon size={16} color={form[doc.key] ? '#00d4aa' : '#475569'} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: form[doc.key] ? '#e2e8f0' : '#94a3b8', fontWeight: '500' }}>
                    {doc.label}
                    {doc.required && <span style={{ color: '#f5a623', marginLeft: '4px', fontSize: '10px' }}>Required for Tier B</span>}
                  </div>
                </div>
                <div style={{
                  fontSize: '9px', fontWeight: '700', padding: '2px 8px', borderRadius: '5px',
                  background: 'rgba(124,111,255,0.1)', color: '#7c6fff', border: '1px solid rgba(124,111,255,0.2)'
                }}>{doc.tier}</div>
              </div>
            ))}

            <div style={{
              background: 'rgba(124,111,255,0.06)', border: '1px solid rgba(124,111,255,0.15)',
              borderRadius: '10px', padding: '14px 16px', marginTop: '8px',
              display: 'flex', gap: '10px', alignItems: 'flex-start'
            }}>
              <Upload size={14} color="#7c6fff" style={{ flexShrink: 0, marginTop: '1px' }} />
              <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.6' }}>
                Actual document upload happens in the secure Deal Room after your NDA is executed.
                We'll send you a document upload link via email within 2 business days.
              </div>
            </div>

            {/* v2.0: Certification readiness checklist */}
            {form.selectedTier !== 'A' && (() => {
              const { items, completionPct } = buildCertChecklist(form, form.selectedTier);
              return (
                <div style={{ marginTop: '20px' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'
                  }}>
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      Certification Readiness
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: '700',
                      color: completionPct >= 80 ? '#00d4aa' : completionPct >= 60 ? '#f5a623' : '#f87171' }}>
                      {completionPct}% complete
                    </div>
                  </div>
                  <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)',
                    borderRadius: '2px', marginBottom: '12px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${completionPct}%`, height: '100%',
                      background: completionPct >= 80 ? '#00d4aa' : completionPct >= 60 ? '#f5a623' : '#f87171',
                      transition: 'width 0.5s ease', borderRadius: '2px'
                    }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {items.map((item, i) => (
                      <div key={item.id} style={{
                        display: 'flex', gap: '10px', alignItems: 'flex-start',
                        padding: '7px 0',
                        borderBottom: i < items.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none'
                      }}>
                        <span style={{
                          fontSize: '12px', flexShrink: 0, marginTop: '1px',
                          color: item.pass ? '#00d4aa' : item.required ? '#f87171' : '#475569'
                        }}>
                          {item.pass ? '✓' : item.required ? '✗' : '○'}
                        </span>
                        <div>
                          <div style={{ fontSize: '11px', color: item.pass ? '#94a3b8' : '#64748b', fontWeight: item.required && !item.pass ? '700' : '400' }}>
                            {item.label}
                            {item.required && !item.pass && (
                              <span style={{ fontSize: '9px', color: '#f87171', marginLeft: '6px', fontWeight: '700' }}>
                                REQUIRED
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '9px', color: '#475569', marginTop: '1px' }}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );

      // ── STEP 6: Contact & Submit ───────────────────────────────────────
      case 'contact':
        return (
          <div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#e2e8f0', marginBottom: '6px', letterSpacing: '-0.02em' }}>Contact & Submit</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '28px' }}>
              Who should we reach out to? This person will be the primary contact for certification and IR setup.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap: '16px' }}>
              <Field label="First Name" required>
                <Input value={form.firstName} onChange={e('firstName')} placeholder="First name" />
              </Field>
              <Field label="Last Name" required>
                <Input value={form.lastName} onChange={e('lastName')} placeholder="Last name" />
              </Field>
              <Field label="Title / Role">
                <Input value={form.title} onChange={e('title')} placeholder="e.g. Commissioner, CEO, CFO" />
              </Field>
              <Field label="Email Address" required>
                <Input type="email" value={form.email} onChange={e('email')} placeholder="you@yourorg.com" style={{...inputStyle, borderColor: fieldErrors.email ? '#f87171' : '#334155'}} />
                <ErrorMsg msg={fieldErrors.email} />
              </Field>
              <Field label="Phone Number">
                <Input type="tel" value={form.phone} onChange={e('phone')} placeholder="+1 (555) 000-0000" style={{...inputStyle, borderColor: fieldErrors.phone ? '#f87171' : '#334155'}} />
                <ErrorMsg msg={fieldErrors.phone} />
              </Field>
              <Field label="LinkedIn Profile">
                <Input value={form.linkedIn} onChange={e('linkedIn')} placeholder="linkedin.com/in/yourname" />
              </Field>
            </div>

            {/* Agreements */}
            <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                {
                  key: 'agreeTerms', required: true,
                  text: 'I agree to InvestorQ\'s Platform Terms of Service and Privacy Policy. I understand that InvestorQ is an intelligence and discovery platform, not a broker-dealer, and does not provide investment advice.'
                },
                form.selectedTier !== 'A' && {
                  key: 'agreeNda', required: true,
                  text: 'I understand that a formal NDA will be sent for e-signature before any financial documents are shared or Tier B/C certification is granted. I agree to execute that NDA in good faith.'
                }
              ].filter(Boolean).map(agreement => (
                <div
                  key={agreement.key}
                  onClick={() => set(agreement.key, !form[agreement.key])}
                  style={{
                    display: 'flex', gap: '12px', alignItems: 'flex-start',
                    padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                    background: form[agreement.key] ? 'rgba(0,212,170,0.05)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${form[agreement.key] ? 'rgba(0,212,170,0.25)' : 'rgba(255,255,255,0.06)'}`
                  }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0, marginTop: '1px',
                    background: form[agreement.key] ? '#00d4aa' : 'transparent',
                    border: `2px solid ${form[agreement.key] ? '#00d4aa' : '#334155'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {form[agreement.key] && <CheckCircle size={12} color="#000" />}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.6' }}>
                    {agreement.required && <span style={{ color: '#f5a623', fontWeight: '700', marginRight: '4px' }}>Required:</span>}
                    {agreement.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Field error summary */}
            {Object.keys(fieldErrors).length > 0 && (
              <div style={{
                background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.3)',
                borderRadius:'10px', padding:'12px 16px', marginBottom:'12px'
              }}>
                <div style={{ fontSize:'11px', fontWeight:'700', color:'#f87171', marginBottom:'6px' }}>
                  ⚠️ Please fix the following before submitting:
                </div>
                {Object.values(fieldErrors).map((err,i) => (
                  <div key={i} style={{ fontSize:'10px', color:'#f87171', marginBottom:'2px' }}>• {err}</div>
                ))}
              </div>
            )}
            {/* v2.0: TierDisclosure — shows tier-specific compliance context */}
            <div style={{ marginTop: '16px' }}>
              <TierDisclosure tier={form.selectedTier} compact />
            </div>

            {/* v2.0: Cert score summary on confirmation step */}
            <div style={{
              marginTop: '12px',
              background: certMeta.bg, border: `1px solid ${certMeta.border}`,
              borderRadius: '10px', padding: '12px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ fontSize: '10px', color: certMeta.color, fontWeight: '800',
                textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Cert Score
              </div>
              <div style={{ fontSize: '16px', fontWeight: '900', color: certMeta.color }}>
                {certScore}/100 <span style={{ fontSize: '10px', fontWeight: '600', color: '#64748b' }}>— {certMeta.label}</span>
              </div>
            </div>

            {/* Summary card */}
            <div style={{
              marginTop: '16px', background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '18px 20px'
            }}>
              <div style={{ fontSize: '10px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '12px' }}>Application Summary</div>
              {[
                { label: 'Organization', value: form.orgName || '—' },
                { label: 'Sport', value: form.sport || '—' },
                { label: 'Tier', value: `${tier.label} — ${tier.name}`, color: tier.color },
                { label: 'Revenue Band', value: form.revenueBand || '—' },
                { label: 'Target Raise', value: form.raiseRange || '—' },
                { label: 'Contact', value: `${form.firstName} ${form.lastName}`.trim() || '—' },
              ].map((row, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '6px 0', borderBottom: i < 5 ? '1px solid rgba(255,255,255,0.04)' : 'none'
                }}>
                  <span style={{ fontSize: '11px', color: '#475569' }}>{row.label}</span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: row.color || '#94a3b8' }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        );

      default: return null;
    }
  };

  // ── Shell ──────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: isMobile ? '12px 10px 60px' : '24px 16px 60px' }}>
      <div style={{ maxWidth: isMobile ? '100%' : '760px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          background: 'rgba(30,41,59,0.6)', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,212,170,0.15)', borderRadius: '20px',
          padding: isMobile ? '16px 16px' : '28px 32px', marginBottom: '20px', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: 'linear-gradient(90deg, #00d4aa, #7c6fff, #f5a623)'
          }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{
                fontSize: '10px', fontWeight: '700', color: '#00d4aa', textTransform: 'uppercase',
                letterSpacing: '0.15em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <Lock size={10} /> InvestorQ Private Market
              </div>
              <div style={{ fontSize: isMobile ? '18px' : '26px', fontWeight: '900', color: '#e2e8f0', letterSpacing: '-0.02em', lineHeight: '1.1' }}>
                Org Application Portal
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>
                Apply for Tier B certification · Get listed on the Sports Private Market
              </div>
            </div>
            <div style={{ display: isMobile ? 'none' : 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['◎ Tier A', '◈ Tier B', '◆ Tier C'].map((t, i) => (
                <div key={i} style={{
                  fontSize: '10px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.08)', color: ['#00d4aa','#7c6fff','#f5a623'][i],
                  background: ['rgba(0,212,170,0.06)','rgba(124,111,255,0.06)','rgba(245,166,35,0.06)'][i]
                }}>{t}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Step progress bar - sticky dots + connectors */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'rgba(10,18,35,0.97)', backdropFilter: 'blur(12px)',
          padding: '14px 0 12px', marginBottom: '24px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          marginLeft: isMobile ? '-10px' : '-16px',
          marginRight: isMobile ? '-10px' : '-16px',
          paddingLeft: isMobile ? '10px' : '16px',
          paddingRight: isMobile ? '10px' : '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {STEPS.map((s, i) => {
              const done = i < step;
              const active = i === step;
              const Icon = s.icon;
              const isLast = i === STEPS.length - 1;
              return (
                <React.Fragment key={s.id}>
                  <div
                    onClick={() => done && setStep(i)}
                    title={s.label}
                    style={{
                      flexShrink: 0,
                      width: active ? 36 : 28,
                      height: active ? 36 : 28,
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: done ? 'pointer' : 'default',
                      background: done ? '#00d4aa' : active ? '#7c6fff' : 'rgba(255,255,255,0.07)',
                      border: `2px solid ${done ? '#00d4aa' : active ? '#7c6fff' : 'rgba(255,255,255,0.12)'}`,
                      boxShadow: active ? '0 0 0 4px rgba(124,111,255,0.2)' : 'none',
                      transition: 'all 0.2s',
                    }}>
                    {done
                      ? <Check size={13} color="#0f172a" strokeWidth={3} />
                      : <Icon size={active ? 15 : 12} color={active ? '#fff' : '#475569'} />
                    }
                  </div>
                  {!isLast && (
                    <div style={{
                      flex: 1, height: '2px', minWidth: 0,
                      background: done ? '#00d4aa' : 'rgba(255,255,255,0.07)',
                      transition: 'background 0.3s',
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c6fff', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {STEPS[step].label}
            </div>
            <div style={{ fontSize: '10px', color: '#475569', fontWeight: '600' }}>
              Step {step + 1} of {STEPS.length}
            </div>
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(30,41,59,0.5)', backdropFilter: 'blur(10px)', border: '1px solid #334155',
          borderRadius: '16px', padding: isMobile ? '16px 14px' : '32px 36px', marginBottom: '20px'
        }}>
          {renderStep()}
        </div>

        {/* Nav buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : setActiveTab && setActiveTab('sports-private')}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '11px 20px', borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.08)', background: 'transparent',
              color: '#64748b', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
            }}>
            <ChevronLeft size={14} />
            {step === 0 ? 'Back to Market' : 'Previous'}
          </button>

          <div style={{ fontSize: '11px', color: '#334155' }}></div>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => canAdvance() && setStep(s => s + 1)}
              disabled={!canAdvance()}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '11px 22px', borderRadius: '10px', border: 'none',
                background: canAdvance()
                  ? 'linear-gradient(135deg, #00d4aa, #00b894)'
                  : 'rgba(255,255,255,0.05)',
                color: canAdvance() ? '#000' : '#334155',
                fontSize: '12px', fontWeight: '700',
                cursor: canAdvance() ? 'pointer' : 'not-allowed'
              }}>
              Continue <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canAdvance() || submitting}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '11px 26px', borderRadius: '10px', border: 'none',
                background: canAdvance() && !submitting
                  ? 'linear-gradient(135deg, #7c6fff, #9b8fff)'
                  : 'rgba(255,255,255,0.05)',
                color: canAdvance() && !submitting ? '#fff' : '#334155',
                fontSize: '12px', fontWeight: '700',
                cursor: canAdvance() && !submitting ? 'pointer' : 'not-allowed'
              }}>
              {submitting ? (
                <>⏳ Submitting…</>
              ) : (
                <><Shield size={14} /> Submit Application</>
              )}
            </button>
          )}
        </div>

        {/* v2.0: Enhanced legal footer */}
        <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '10px', color: '#475569', lineHeight: '1.7' }}>
          InvestorQ is a financial intelligence and discovery platform operated by OffBeet Media &amp; Tech Inc.
          InvestorQ is <strong>not a registered broker-dealer, funding portal, or investment adviser.</strong>{' '}
          Certification is not a guarantee of investment suitability or returns.
          Capital raises are facilitated exclusively by licensed broker-dealer partners (DealMaker — FINRA CRD #315324; Republic — SEC-registered funding portal).
          InvestorQ does not handle investor funds or securities.
          Submitting this application does not create any obligation on InvestorQ or the applicant.
          All private market investments are illiquid and speculative. You may lose your entire investment.{' '}
          <a href="/legal/terms" style={{ color: '#64748b', textDecoration: 'underline' }}>Terms</a>
          {' '}·{' '}
          <a href="/legal/privacy" style={{ color: '#64748b', textDecoration: 'underline' }}>Privacy</a>
          {' '}·{' '}
          <a href="/legal/disclosures" style={{ color: '#64748b', textDecoration: 'underline' }}>Full Disclosures</a>
        </div>
      </div>
    </div>
  );
};

export default OrgApplicationPortal;